import { Component, Injector, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { SimpleModalService } from '@looorent/ngx-simple-modal';
import { ToastrService } from 'ngx-toastr';
import { ModelEditor } from 'src/app/shared/editors/modeleditor';
import { ApiServiceBase } from 'src/app/Services/api.service.base';
import { StructureSelectorComponent } from 'src/app/shared/modals/StructureSelector/structureselector.component';
import { OrganizationsService } from 'src/app/Services/organizations.service';

@Component({
    selector: 'app-editdatafeed',
    templateUrl: './editdatafeed.component.html',
    styleUrls: ['./editdatafeed.component.scss'],
    standalone: false
})
export class EditDataFeedComponent extends ModelEditor implements OnInit {
  public modalService: SimpleModalService;
  public awsEndpoints: any = [];
  public showSecret: boolean = false;
  public currentOrgId: string = '';
  public currentOrgName: string = '';
  public formattedCurrentDate: string = '';
  public showKeyFileInput: boolean = false;
  public hasExistingKey: boolean = false;

  constructor(
    private apiService: ApiServiceBase, 
    private injector: Injector, 
    modalService: SimpleModalService,
    private organizationsService: OrganizationsService
  ) {
    super("integrations/datafeeds", injector);
    this.modalService = modalService;
    
    // Fetch AWS endpoints
    apiService.Get("integrations/datafeeds/kinesis/endpoints").then(result => {
      if (result != null) {
        this.awsEndpoints = result;
      }
    });

    // Get current organization info using the GetOrganization method
    this.organizationsService.GetOrganization().then((org: any) => {
      if (org) {
        this.currentOrgId = org.Id || '';
        this.currentOrgName = org.Name || '';
        
        // Update the JSON examples after we have the organization data
        this.updateJsonExamples();
      }
    });
    
    // Format current date in ISO format with Z suffix (UTC)
    this.formattedCurrentDate = this.formatDateForJson(new Date());
  }

  // Update JSON examples with current org data and date
  private updateJsonExamples(): void {
    this.spacesFormatExample = `{
    "Frogs": [
        { 
            "Id": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
            "ChipId": 12345,
            "BayName": "A-123",
            "LastCheckedInOn": "${this.formattedCurrentDate}",
            "LastUpdatedOn": "${this.formattedCurrentDate}",
            "State": 0, // 0=Vacant, 1=Occupied, 2=Unknown
            "UniqueName": "A-123",
            "Location": {
                "Latitude": 37.7749,
                "Longitude": -122.4194
            },
            "Level": 1,
            "OccupancyId": 42,
            "RegionName": "Section A",
            "AssociatedPlates": "ABC123,XYZ789",
            "OrgId": "${this.currentOrgId}",
            "OrgName": "${this.currentOrgName}"
        }
    ],
    "UpdatedOn": "${this.formattedCurrentDate}"
}`;

    this.regionFormatExample = `{
    "Regions": [
        {
            "RegionId": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
            "Occupied": 42,
            "Vacant": 58,
            "RegionName": "Section A",
            "UpdatedOn": "${this.formattedCurrentDate}",
            "OrgId": "${this.currentOrgId}",
            "OrgName": "${this.currentOrgName}"
        }
    ]
}`;
  }

  public currentDate : Date = new Date();

  // Format date to YYYYMMDDTHHMMSSZ format
  private formatDateForJson(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
  }

  public override DefaultModel(): any {
    return {
      Description: "",
      DataFeedTarget: "kinesis", // Default to kinesis
      // Kinesis fields
      FeedType: 0,
      AWSKey: "",
      AWSSecret: "",
      AWSEndpoint: "",
      StreamName: "",
      PartitionKey: "",
      // SFTP fields
      SFTPHost: "",
      SFTPPath: "",
      SFTPUsername: "",
      SFTPPassword: "",
      SFTPKey: "",
      DispatchSchedule: "",
      // Common fields
      Structures: []
    }
  }

  public override AfterModelLoaded(): void {
    // If we're in edit mode, disable the DataFeedTarget field
    if (!this.ModelIsNew) {
      this.Form.get('DataFeedTarget')?.disable();
    }
    
    // Check if there's an existing SFTP key
    this.hasExistingKey = !!this.Form.get('SFTPKey')?.value;
  }

  public override BeforeSave(): void {
    // If DataFeedTarget is disabled (in edit mode), we need to enable it temporarily
    // so its value is included when the form is submitted
    const targetControl = this.Form.get('DataFeedTarget');
    if (targetControl?.disabled) {
      targetControl.enable();
    }
  }

  public override Validators(): any {
    return {
      "DataFeedTarget": [Validators.required],
      "Structures": [Validators.required],
      // Validators will be conditionally applied based on the selected target
    };
  }

  public isKinesisTarget(): boolean {
    return this.Form?.get('DataFeedTarget')?.value === 'kinesis';
  }

  public isSFTPTarget(): boolean {
    return this.Form?.get('DataFeedTarget')?.value === 'sftp';
  }

  public SelectParkingStructure(): void {
    this.modalService.addModal(StructureSelectorComponent, {
      StructureType: "ParkingLot",
      IsMultiSelect: true,
      Structures: this.Form.get('Structures')?.value || []
    })
    .subscribe((result) => {
      if (result != null && result.length > 0) {
        const structureArray = this.FormArray(this.Form, 'Structures');
        
        result.forEach((structure: any) => {
          const exists = structureArray.controls.some(
            (control) => control.value.Id === structure.Id
          );
          
          if (!exists) {
            structureArray.push(new FormControl({
              Id: structure.Id,
              Name: structure.Name,
              LocationString: structure.LocationString
            }));
          }
        });
        
        this.Form.markAsDirty();
      }
    });
  }

  public removeStructure(index: number): void {
    this.FormArray(this.Form, 'Structures').removeAt(index);
    this.Form.markAsDirty();
  }

  public toggleSecretVisibility(): void {
    this.showSecret = !this.showSecret;
  }

  public toggleKeyFileInput(): void {
    this.showKeyFileInput = !this.showKeyFileInput;
  }

  public handleKeyFileUpload(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const fileContent = e.target.result;
        this.Form.get('SFTPKey')?.setValue(fileContent);
        this.Form.get('SFTPKey')?.markAsDirty();
        this.hasExistingKey = true;
        this.showKeyFileInput = false;
      };
      reader.readAsText(file);
    }
  }

  // Initialize the example strings with placeholders that will be updated
  public spacesFormatExample: string = '';
  public regionFormatExample: string = '';

  // Get the current feed type for display purposes only
  public getCurrentFeedType(): number {
    return this.Form?.get('FeedType')?.value || 0;
  }
}
